import React, { useState, useEffect } from 'react';

interface BillingData {
  currentMonth: {
    month: string;
    totalProducts: number;
    amountDue: number;
    status: 'pending' | 'paid' | 'overdue';
    dueDate: string;
  };
  billingHistory: Array<{
    id: string;
    month: string;
    totalProducts: number;
    amount: number;
    status: 'pending' | 'paid' | 'overdue';
    paidDate?: string;
    dueDate: string;
  }>;
  paymentMethods: Array<{
    id: string;
    type: 'mpesa' | 'bank' | 'card';
    details: string;
    isDefault: boolean;
  }>;
  usageBreakdown: Array<{
    productName: string;
    category: string;
    quantity: number;
    cost: number;
  }>;
  monthlyStats: {
    totalSpent: number;
    averageMonthly: number;
    productsProtected: number;
    savings: number;
  };
}

const BillingDashboard: React.FC = () => {
  const [billingData, setBillingData] = useState<BillingData>({
    currentMonth: {
      month: 'November 2024',
      totalProducts: 4850,
      amountDue: 4850,
      status: 'pending',
      dueDate: '2024-12-01'
    },
    billingHistory: [],
    paymentMethods: [],
    usageBreakdown: [],
    monthlyStats: {
      totalSpent: 0,
      averageMonthly: 0,
      productsProtected: 0,
      savings: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  // Animation styles
  const animationStyles = `
    .fade-in-up {
      animation: fadeInUp 0.8s ease-out forwards;
    }
    
    .slide-in-left {
      animation: slideInLeft 0.8s ease-out forwards;
    }
    
    .scale-in {
      animation: scaleIn 0.6s ease-out forwards;
    }
    
    .pulse {
      animation: pulse 2s ease-in-out infinite;
    }
    
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes slideInLeft {
      from {
        opacity: 0;
        transform: translateX(-50px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    
    @keyframes scaleIn {
      from {
        opacity: 0;
        transform: scale(0.9);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    
    @keyframes pulse {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.05);
      }
    }
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    
    .hover-lift {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    .hover-lift:hover {
      transform: translateY(-4px);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    }
  `;

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      // Enhanced simulated data for universal platform
      setTimeout(() => {
        setBillingData({
          currentMonth: {
            month: 'November 2024',
            totalProducts: 4850,
            amountDue: 4850,
            status: 'pending',
            dueDate: '2024-12-01'
          },
          billingHistory: [
            {
              id: '1',
              month: 'October 2024',
              totalProducts: 3240,
              amount: 3240,
              status: 'paid',
              paidDate: '2024-10-28',
              dueDate: '2024-11-01'
            },
            {
              id: '2',
              month: 'September 2024',
              totalProducts: 2890,
              amount: 2890,
              status: 'paid',
              paidDate: '2024-09-30',
              dueDate: '2024-10-01'
            },
            {
              id: '3',
              month: 'August 2024',
              totalProducts: 2560,
              amount: 2560,
              status: 'paid',
              paidDate: '2024-08-29',
              dueDate: '2024-09-01'
            }
          ],
          paymentMethods: [
            {
              id: '1',
              type: 'mpesa',
              details: '+254 XXX XXX XXX',
              isDefault: true
            },
            {
              id: '2',
              type: 'bank',
              details: 'KCB Bank - ****1234',
              isDefault: false
            },
            {
              id: '3',
              type: 'card',
              details: 'Visa - ****5678',
              isDefault: false
            }
          ],
          usageBreakdown: [
            { productName: 'Premium Vodka 750ml', category: 'Alcohol & Beverages', quantity: 1500, cost: 1500 },
            { productName: 'Organic Face Cream 50ml', category: 'Cosmetics & Personal Care', quantity: 1200, cost: 1200 },
            { productName: 'Smartphone XR Pro', category: 'Electronics', quantity: 800, cost: 800 },
            { productName: 'Premium Coffee Beans 500g', category: 'Food & Snacks', quantity: 750, cost: 750 },
            { productName: 'Pain Relief Tablets', category: 'Pharmaceuticals', quantity: 600, cost: 600 }
          ],
          monthlyStats: {
            totalSpent: 13540,
            averageMonthly: 2708,
            productsProtected: 13540,
            savings: 2500
          }
        });
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setPaymentProcessing(true);
    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Update billing data to show as paid
      setBillingData(prev => ({
        ...prev,
        currentMonth: {
          ...prev.currentMonth,
          status: 'paid'
        }
      }));
      
      alert('Payment successful! Your invoice has been paid.');
    } catch (error) {
      alert('Payment failed. Please try again.');
    } finally {
      setPaymentProcessing(false);
      setShowPaymentModal(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return '#22c55e';
      case 'pending': return '#f59e0b';
      case 'overdue': return '#ef4444';
      default: return '#64748b';
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'paid': return '#f0fdf4';
      case 'pending': return '#fef3c7';
      case 'overdue': return '#fef2f2';
      default: return '#f8fafc';
    }
  };

  if (loading && billingData.billingHistory.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        padding: '40px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '24px',
          padding: '60px',
          textAlign: 'center',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 24px'
          }}></div>
          <h3 style={{ color: '#1e293b', margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600' }}>
            Loading Billing Data
          </h3>
          <p style={{ color: '#64748b', margin: 0, fontSize: '16px' }}>
            Please wait while we fetch your billing information
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <style>{animationStyles}</style>
      
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                         radial-gradient(circle at 80% 20%, rgba(6, 182, 212, 0.1) 0%, transparent 50%),
                         radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)`,
      }}></div>

      <div style={{
        position: 'relative',
        zIndex: 1,
        padding: '40px 20px'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {/* Header */}
          <div className="fade-in-up" style={{
            textAlign: 'center',
            marginBottom: '60px'
          }}>
            <div style={{
              display: 'inline-block',
              padding: '8px 24px',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '50px',
              marginBottom: '24px'
            }}>
              <span style={{
                color: '#3b82f6',
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Billing & Payments
              </span>
            </div>

            <h1 style={{
              fontSize: '3.5rem',
              fontWeight: '800',
              color: 'white',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em'
            }}>
              Billing{' '}
              <span style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Dashboard
              </span>
            </h1>

            <p style={{
              fontSize: '20px',
              color: '#94a3b8',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Manage your ScanOnce 2.0 subscription, view usage analytics, 
              and handle payments seamlessly.
            </p>
          </div>

          {/* Stats Overview */}
          <div className="slide-in-left" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '24px',
            marginBottom: '40px'
          }}>
            {[
              { 
                title: 'Total Spent', 
                value: `KSh ${billingData.monthlyStats.totalSpent.toLocaleString()}`, 
                icon: '💰',
                color: '#3b82f6',
                desc: 'All-time spending'
              },
              { 
                title: 'Monthly Average', 
                value: `KSh ${billingData.monthlyStats.averageMonthly.toLocaleString()}`, 
                icon: '📊',
                color: '#06b6d4',
                desc: 'Average per month'
              },
              { 
                title: 'Products Protected', 
                value: billingData.monthlyStats.productsProtected.toLocaleString(), 
                icon: '🛡️',
                color: '#22c55e',
                desc: 'Total units secured'
              },
              { 
                title: 'Cost Savings', 
                value: `KSh ${billingData.monthlyStats.savings.toLocaleString()}`, 
                icon: '💡',
                color: '#8b5cf6',
                desc: 'Vs traditional methods'
              }
            ].map((stat, index) => (
              <div key={index} className="hover-lift" style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                padding: '32px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 100%)`,
                  borderRadius: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '24px'
                }}>
                  {stat.icon}
                </div>
                <h3 style={{
                  color: 'white',
                  fontSize: '2rem',
                  fontWeight: '700',
                  marginBottom: '8px'
                }}>
                  {stat.value}
                </h3>
                <p style={{
                  color: '#94a3b8',
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '4px'
                }}>
                  {stat.title}
                </p>
                <p style={{
                  color: '#64748b',
                  fontSize: '14px',
                  margin: 0
                }}>
                  {stat.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Current Month Bill - Enhanced */}
          <div className="fade-in-up" style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: billingData.currentMonth.status === 'overdue' 
              ? '2px solid #ef4444' 
              : '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '24px',
            padding: '48px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            marginBottom: '40px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: '32px',
              marginBottom: '40px'
            }}>
              <div>
                <h2 style={{
                  color: '#1e293b',
                  fontSize: '2rem',
                  fontWeight: '700',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                  Current Bill - {billingData.currentMonth.month}
                </h2>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  marginBottom: '16px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    backgroundColor: getStatusBg(billingData.currentMonth.status),
                    color: getStatusColor(billingData.currentMonth.status),
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    {billingData.currentMonth.status === 'paid' && '✅'}
                    {billingData.currentMonth.status === 'pending' && '⏳'}
                    {billingData.currentMonth.status === 'overdue' && '⚠️'}
                    {billingData.currentMonth.status}
                  </span>
                  <span style={{
                    color: '#64748b',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12,6 12,12 16,14"/>
                    </svg>
                    Due: {new Date(billingData.currentMonth.dueDate).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div style={{ textAlign: 'right' }}>
                <p style={{
                  color: '#64748b',
                  fontSize: '16px',
                  margin: '0 0 8px 0'
                }}>
                  Amount Due
                </p>
                <p style={{
                  fontSize: '3rem',
                  fontWeight: '800',
                  color: '#1e293b',
                  margin: '0 0 24px 0',
                  lineHeight: '1'
                }}>
                  KSh {billingData.currentMonth.amountDue.toLocaleString()}
                </p>
                
                {billingData.currentMonth.status !== 'paid' && (
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="hover-lift"
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                      color: 'white',
                      padding: '16px 32px',
                      border: 'none',
                      borderRadius: '12px',
                      fontWeight: '600',
                      fontSize: '16px',
                      cursor: 'pointer',
                      boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                      <line x1="1" y1="10" x2="23" y2="10"/>
                    </svg>
                    Pay Now
                  </button>
                )}
              </div>
            </div>

            {/* Enhanced Usage Breakdown */}
            <div style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              padding: '32px',
              borderRadius: '16px',
              border: '1px solid #e2e8f0'
            }}>
              <h3 style={{
                color: '#1e293b',
                fontSize: '1.3rem',
                fontWeight: '600',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18"/>
                  <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
                </svg>
                Usage Breakdown by Category
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '20px',
                marginBottom: '24px'
              }}>
                {billingData.usageBreakdown.map((item, index) => (
                  <div key={index} className="hover-lift" style={{
                    backgroundColor: 'white',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '12px'
                    }}>
                      <div>
                        <h4 style={{
                          color: '#1e293b',
                          fontSize: '16px',
                          fontWeight: '600',
                          marginBottom: '4px'
                        }}>
                          {item.productName}
                        </h4>
                        <p style={{
                          color: '#3b82f6',
                          fontSize: '12px',
                          fontWeight: '500',
                          margin: 0,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {item.category}
                        </p>
                      </div>
                      <div style={{
                        textAlign: 'right'
                      }}>
                        <div style={{
                          color: '#1e293b',
                          fontSize: '18px',
                          fontWeight: '700'
                        }}>
                          KSh {item.cost.toLocaleString()}
                        </div>
                        <div style={{
                          color: '#64748b',
                          fontSize: '12px'
                        }}>
                          {item.quantity.toLocaleString()} units
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress bar */}
                    <div style={{
                      width: '100%',
                      height: '6px',
                      backgroundColor: '#e2e8f0',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${(item.cost / billingData.currentMonth.amountDue) * 100}%`,
                        height: '100%',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                        borderRadius: '3px'
                      }}></div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{
                borderTop: '2px solid #e2e8f0',
                paddingTop: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '16px'
              }}>
                <div>
                  <span style={{
                    color: '#1e293b',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}>
                    Total: {billingData.currentMonth.totalProducts.toLocaleString()} products protected
                  </span>
                  <div style={{
                    color: '#64748b',
                    fontSize: '14px',
                    marginTop: '4px'
                  }}>
                    Across {billingData.usageBreakdown.length} product categories
                  </div>
                </div>
                <div style={{
                  textAlign: 'right'
                }}>
                  <div style={{
                    color: '#1e293b',
                    fontSize: '24px',
                    fontWeight: '800'
                  }}>
                    KSh {billingData.currentMonth.amountDue.toLocaleString()}
                  </div>
                  <div style={{
                    color: '#22c55e',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    @ KSh 1 per unit
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Billing History & Payment Methods Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
            gap: '40px',
            marginBottom: '40px'
          }}>
            {/* Enhanced Billing History */}
            <div className="fade-in-up" style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '24px',
              padding: '32px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px'
              }}>
                <h3 style={{
                  color: '#1e293b',
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 3h18v18H3zM21 9H3"/>
                  </svg>
                  Billing History
                </h3>
                <button
                  onClick={() => alert('Export functionality coming soon!')}
                  style={{
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    padding: '8px 16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Export
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse'
                }}>
                  <thead>
                    <tr style={{
                      backgroundColor: '#f8fafc',
                      borderBottom: '2px solid #e2e8f0'
                    }}>
                      {['Month', 'Products', 'Amount', 'Status', 'Actions'].map((header) => (
                        <th key={header} style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          color: '#374151',
                          fontWeight: '600',
                          fontSize: '14px'
                        }}>
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {billingData.billingHistory.map((bill) => (
                      <tr key={bill.id} style={{
                        borderBottom: '1px solid #e2e8f0',
                        transition: 'background-color 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}>
                        <td style={{
                          padding: '16px',
                          color: '#1e293b',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}>
                          {bill.month}
                        </td>
                        <td style={{
                          padding: '16px',
                          color: '#64748b',
                          fontSize: '14px'
                        }}>
                          {bill.totalProducts.toLocaleString()}
                        </td>
                        <td style={{
                          padding: '16px',
                          color: '#1e293b',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}>
                          KSh {bill.amount.toLocaleString()}
                        </td>
                        <td style={{
                          padding: '16px'
                        }}>
                          <span style={{
                            padding: '6px 12px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            backgroundColor: getStatusBg(bill.status),
                            color: getStatusColor(bill.status),
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            {bill.status === 'paid' && '✅'}
                            {bill.status === 'pending' && '⏳'}
                            {bill.status === 'overdue' && '⚠️'}
                            {bill.status}
                          </span>
                        </td>
                        <td style={{
                          padding: '16px'
                        }}>
                          <button
                            onClick={() => alert('Download invoice functionality coming soon!')}
                            style={{
                              backgroundColor: 'transparent',
                              color: '#3b82f6',
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              transition: 'color 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#1e40af'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#3b82f6'}
                          >
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Enhanced Payment Methods */}
            <div className="fade-in-up" style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '24px',
              padding: '32px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px'
              }}>
                <h3 style={{
                  color: '#1e293b',
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                  Payment Methods
                </h3>
                <button
                  onClick={() => alert('Add payment method functionality coming soon!')}
                  className="hover-lift"
                  style={{
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    color: 'white',
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add Method
                </button>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {billingData.paymentMethods.map((method) => (
                  <div key={method.id} className="hover-lift" style={{
                    padding: '24px',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    border: method.isDefault ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                    borderRadius: '16px',
                    position: 'relative',
                    transition: 'all 0.3s ease'
                  }}>
                    {method.isDefault && (
                      <div style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Default
                      </div>
                    )}
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      marginBottom: '16px'
                    }}>
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '12px',
                        background: method.type === 'mpesa' 
                          ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' 
                          : method.type === 'bank' 
                          ? 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' 
                          : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '20px'
                      }}>
                        {method.type === 'mpesa' ? '📱' : 
                         method.type === 'bank' ? '🏦' : '💳'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          color: '#1e293b',
                          fontSize: '16px',
                          fontWeight: '600',
                          margin: '0 0 4px 0',
                          textTransform: 'capitalize'
                        }}>
                          {method.type === 'mpesa' ? 'M-Pesa Mobile Money' : 
                           method.type === 'bank' ? 'Bank Account' : 'Credit/Debit Card'}
                        </h4>
                        <p style={{
                          color: '#64748b',
                          fontSize: '14px',
                          margin: 0,
                          fontFamily: 'monospace'
                        }}>
                          {method.details}
                        </p>
                      </div>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      gap: '12px'
                    }}>
                      <button
                        onClick={() => alert('Edit payment method functionality coming soon!')}
                        style={{
                          flex: 1,
                          backgroundColor: 'white',
                          color: '#475569',
                          padding: '8px 16px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#3b82f6';
                          e.currentTarget.style.color = '#3b82f6';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.color = '#475569';
                        }}
                      >
                        Edit
                      </button>
                      {!method.isDefault && (
                        <button
                          onClick={() => alert('Remove payment method functionality coming soon!')}
                          style={{
                            backgroundColor: '#fef2f2',
                            color: '#ef4444',
                            padding: '8px 16px',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            fontWeight: '600',
                            fontSize: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#ef4444';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fef2f2';
                            e.currentTarget.style.color = '#ef4444';
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Enhanced Payment Modal */}
          {showPaymentModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px',
              backdropFilter: 'blur(5px)'
            }}>
              <div className="scale-in" style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '24px',
                padding: '48px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                maxWidth: '600px',
                width: '100%',
                position: 'relative'
              }}>
                {paymentProcessing && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      border: '4px solid #f3f4f6',
                      borderTop: '4px solid #3b82f6',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginBottom: '24px'
                    }}></div>
                    <h3 style={{ color: '#1e293b', marginBottom: '8px', fontSize: '20px', fontWeight: '600' }}>
                      Processing Payment
                    </h3>
                    <p style={{ color: '#64748b', fontSize: '16px', textAlign: 'center' }}>
                      Please wait while we process your payment securely...
                    </p>
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '32px'
                }}>
                  <h2 style={{
                    color: '#1e293b',
                    fontSize: '2rem',
                    fontWeight: '700',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                      <line x1="1" y1="10" x2="23" y2="10"/>
                    </svg>
                    Pay Invoice
                  </h2>
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      fontSize: '28px',
                      cursor: 'pointer',
                      color: '#64748b',
                      transition: 'color 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
                  >
                    ×
                  </button>
                </div>

                {/* Enhanced Invoice Summary */}
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                  padding: '32px',
                  borderRadius: '16px',
                  border: '2px solid #3b82f6',
                  marginBottom: '32px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <span style={{ 
                      color: '#64748b', 
                      fontSize: '16px',
                      fontWeight: '500'
                    }}>
                      Invoice Amount:
                    </span>
                    <span style={{ 
                      color: '#1e293b', 
                      fontSize: '2.5rem',
                      fontWeight: '800'
                    }}>
                      KSh {billingData.currentMonth.amountDue.toLocaleString()}
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '16px',
                    paddingTop: '16px',
                    borderTop: '1px solid #e2e8f0'
                  }}>
                    <div>
                      <div style={{ color: '#64748b', fontSize: '14px', marginBottom: '4px' }}>
                        Products Protected
                      </div>
                      <div style={{ color: '#1e293b', fontSize: '18px', fontWeight: '600' }}>
                        {billingData.currentMonth.totalProducts.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#64748b', fontSize: '14px', marginBottom: '4px' }}>
                        Due Date
                      </div>
                      <div style={{ color: '#1e293b', fontSize: '18px', fontWeight: '600' }}>
                        {new Date(billingData.currentMonth.dueDate).toLocaleDateString()}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#64748b', fontSize: '14px', marginBottom: '4px' }}>
                        Rate per Unit
                      </div>
                      <div style={{ color: '#22c55e', fontSize: '18px', fontWeight: '600' }}>
                        KSh 1.00
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enhanced Payment Method Selection */}
                <div style={{ marginBottom: '32px' }}>
                  <h4 style={{
                    color: '#374151',
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '20px'
                  }}>
                    Select Payment Method:
                  </h4>
                  
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                  }}>
                    {billingData.paymentMethods.map((method) => (
                      <label key={method.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        padding: '20px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        backgroundColor: 'white'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#3b82f6';
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.backgroundColor = 'white';
                      }}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={method.id}
                          defaultChecked={method.isDefault}
                          style={{ 
                            margin: 0,
                            width: '20px',
                            height: '20px',
                            accentColor: '#3b82f6'
                          }}
                        />
                        <div style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '12px',
                          background: method.type === 'mpesa' 
                            ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' 
                            : method.type === 'bank' 
                            ? 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' 
                            : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '20px'
                        }}>
                          {method.type === 'mpesa' ? '📱' : 
                           method.type === 'bank' ? '🏦' : '💳'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{
                            color: '#1e293b',
                            fontSize: '16px',
                            fontWeight: '600',
                            margin: '0 0 4px 0'
                          }}>
                            {method.type === 'mpesa' ? 'M-Pesa Mobile Money' : 
                             method.type === 'bank' ? 'Bank Account Transfer' : 'Credit/Debit Card'}
                          </p>
                          <p style={{
                            color: '#64748b',
                            fontSize: '14px',
                            margin: 0,
                            fontFamily: 'monospace'
                          }}>
                            {method.details}
                          </p>
                        </div>
                        {method.isDefault && (
                          <div style={{
                            background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            Default
                          </div>
                        )}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Enhanced Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    style={{
                      backgroundColor: '#f1f5f9',
                      color: '#475569',
                      padding: '16px 32px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontWeight: '600',
                      fontSize: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#cbd5e1';
                      e.currentTarget.style.backgroundColor = '#e2e8f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePayment}
                    disabled={paymentProcessing}
                    className="hover-lift"
                    style={{
                      background: paymentProcessing 
                        ? '#9ca3af' 
                        : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                      color: 'white',
                      padding: '16px 32px',
                      border: 'none',
                      borderRadius: '12px',
                      fontWeight: '600',
                      fontSize: '16px',
                      cursor: paymentProcessing ? 'not-allowed' : 'pointer',
                      opacity: paymentProcessing ? 0.7 : 1,
                      boxShadow: paymentProcessing 
                        ? 'none' 
                        : '0 4px 15px rgba(59, 130, 246, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: '200px',
                      justifyContent: 'center'
                    }}
                  >
                    {paymentProcessing ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 12l2 2 4-4"/>
                          <circle cx="12" cy="12" r="9"/>
                        </svg>
                        Pay KSh {billingData.currentMonth.amountDue.toLocaleString()}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingDashboard;


